import { Box, useTheme } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

const documentProcessingBorderAnimation = keyframes`
  from {
    --document-processing-border-angle: 0deg;
  }

  to {
    --document-processing-border-angle: 360deg;
  }
`;

const getDocumentProcessingGradient = (theme: Theme) => `conic-gradient(
  from var(--document-processing-border-angle),
  ${theme.palette.primary.dark},
  ${theme.palette.primary.main},
  ${theme.palette.primary.light},
  ${theme.palette.secondary.main},
  ${theme.palette.error.main},
  ${theme.palette.warning.main},
  ${theme.palette.primary.dark}
)`;

const reducedMotionStyles = {
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
} as const;

type DocumentProcessingBorderOptions = {
  theme: Theme;
  isProcessing: boolean;
  padding: string;
};

export const getDocumentProcessingBorderStyles = ({
  theme,
  isProcessing,
  padding,
}: DocumentProcessingBorderOptions) => ({
  content: '""',
  position: 'absolute',
  inset: 0,
  padding,
  borderRadius: 'inherit',
  pointerEvents: 'none',
  background: getDocumentProcessingGradient(theme),
  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  maskComposite: 'exclude',
  opacity: isProcessing ? 1 : 0,
  animation: `${documentProcessingBorderAnimation} 2.5s linear infinite`,
  animationPlayState: isProcessing ? 'running' : 'paused',
  transition: 'opacity 300ms ease-out',
  ...reducedMotionStyles,
});

export const DocumentProcessingSpinner = () => {

  const theme = useTheme();

  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: 20,
        height: 20,
        flex: '0 0 auto',
        borderRadius: '50%',
        background: getDocumentProcessingGradient(theme),
        WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 0)',
        mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 0)',
        animation: `${documentProcessingBorderAnimation} 2.5s linear infinite`,
        ...reducedMotionStyles,
      }}
    />
  );

};
